/** The town screen's slot grid, ported from `TOWNS/tools/town_layout.py`.
 *
 * A town screen is one painted background plus a set of building sprites at
 * fixed positions. Every faction maps its buildings onto the same named slots,
 * so a faction's screen is laid out the moment its buildings exist and the
 * renderer is a sort plus a loop.
 *
 * Slots are (centre x, ground y, width) on a 1280x720 canvas: `x` is the
 * sprite's horizontal CENTRE, `y` its GROUND LINE. Height is whatever the art
 * needs. Draw order is by ground line, so a building nearer the viewer overlaps
 * one behind it; ties break on x.
 *
 * Pure: no DOM, no React, no kernel rule module.
 */

import type { BuildingDef, BuildingDefId, ContentRegistry } from "@game/core";
import { TOWN_LAYOUTS } from "./townLayouts.js";

export const TOWN_CANVAS_W = 1280;
export const TOWN_CANVAS_H = 720;

/**
 * THE HI-DEF CONTRACT (2026-09-04).
 *
 * The slot grid stays in 1280x720 canvas units, but the art ships at the
 * size it is DRAWN at on a 1080p screen: the stage scales the canvas by 3/2
 * there (`stageLayout`), so every building sprite is built at drawn width x
 * TOWN_HD (TOWNS/tools/build_town.py, `town_layout.hd_width`) and the
 * backdrop at 1920x1080, and the screen draws them with linear filtering at
 * 1/TOWN_HD (TownBuilding, townScene.TownBackdrop). Nothing is quantised to a
 * palette and nothing is NEAREST-upscaled; the pixel-art look stays on the
 * adventure map and the battlefield. Mirrors `town_layout.HD`.
 */
export const TOWN_HD = 1.5;

export interface TownSlot {
  readonly name: string;
  /** Horizontal centre, canvas pixels. */
  readonly x: number;
  /** Ground line - the sprite's bottom edge, not its top. */
  readonly y: number;
  /** BUILD width: the sprite ships at this many pixels wide. */
  readonly w: number;
  readonly band: "back" | "middle" | "front";
  /**
   * How much larger than its band this one structure is drawn.
   *
   * The fortification is the town's anchor in the classic - it is the biggest thing
   * on the screen, it stands at the top centre against the sky, and everything
   * else is arranged below it. Its art ships at 225px like a hall, so the
   * emphasis is applied here rather than asked of the painter: 1.35 on the
   * back band's 2/3 draws it at 0.9, 202px wide and up to 245px tall, which at
   * the 3/2 stage is the keep of the reference Castle screen to within a few
   * device pixels. The Grail beside it takes 4/3.
   */
  readonly emph?: number;
  /**
   * The drawn scale, when the slot comes from a faction's own solved layout
   * (townLayouts.ts). It replaces the band's scale and the emphasis: a
   * per-faction composition puts a keep on a far cliff at 0.55 and a hut on
   * the near shore at 1.2, and the band is then only the haze class.
   */
  readonly scale?: number;
  /**
   * The art's height at scale 1, canvas pixels, when the slot comes from a
   * faction's own layout: measured off the raw painting by
   * TOWNS/tools/sprite_measure.py and carried through townLayouts.ts, so
   * the screen's boxes, the tests' gates and the solver's are all the same
   * boxes. The shared table's 75th-percentile ratio said a mage guild is
   * 1.54 times as tall as wide; the painted five-tier spire is 2.0, and the
   * gate that passed on the table composited a hut onto its roof.
   */
  readonly h?: number;
}

/**
 * The three shelves the painting is built on, in canvas rows.
 *
 * The stage covers a ~2.4:1 box and crops the sky, so rows 0-~200 exist only
 * for a tall window (see `stageLayout`); the composition is for rows 200-720.
 * The horizon - where the far distance meets the top shelf - is at 345, the
 * scarp faces between shelves start at 465 and 600. Every backdrop is warped
 * onto exactly these rows by TOWNS/tools/build_town.py, which is what lets
 * one slot table serve thirteen paintings. The life layer (townAnim.ts) puts
 * its clouds and birds above HORIZON_Y and its fog along the scarps.
 */
export const HORIZON_Y = 345;
export const SCARP_Y: readonly [number, number] = Object.freeze([465, 600] as const);

/**
 * The 19 slots, on three TERRACES.
 *
 * Ported from `TOWNS/tools/town_layout.py`, where the table was solved
 * (2026-09-03) by a hill-climb over band order and per-slot ground stagger
 * under `spreadSlots`' own rule, with one gate: no box may lose more than 30%
 * of itself to nearer boxes, on the full 19-slot spread and on every faction's
 * own subset. Worst case on this table is the Hall at 27%, to the tier-7
 * dwelling in front of it.
 *
 * Back band on the top shelf, feet at 428-437, drawn at 2/3 with the fort at
 * the top centre; middle band on the middle shelf, feet at 545-564; front band
 * on the near shelf, feet at 710-716, drawn at 4/3. The ground lines inside a
 * band are staggered so two neighbours stand at visibly different depths and
 * the nearer laps the farther one's apron; `y` here already includes the
 * terrace bow at the table's `x`, and a re-spread slot slides along the curve.
 *
 * Build widths came DOWN with the terraces (a middle dwelling was 165-195, it
 * is 120-130) so the drawn sizes match the reference: the keep is the one big
 * thing and a dwelling is about a sixth of the visible frame's height rather
 * than a third. The art is rebuilt at these widths, so nothing is drawn
 * under 2/3 of its own pixels.
 */
export const TOWN_SLOTS: readonly TownSlot[] = Object.freeze([
  // back band - the top shelf, just under the horizon
  { name: "resource_silo", x: 45, y: 437, w: 135, band: "back" },
  { name: "special_3", x: 183, y: 433, w: 140, band: "back" },
  { name: "dwelling_2", x: 327, y: 430, w: 150, band: "back" },
  { name: "mage_guild", x: 484, y: 429, w: 180, band: "back" },
  { name: "fort", x: 692, y: 428, w: 225, band: "back", emph: 1.35 },
  // The Grail stands BESIDE the keep, then the two specials. With the
  // specials first, a faction that has all of them (Saltreach) spread special_5
  // flush against the keep and directly behind the hall - 57% buried - while
  // the all-19 table passed. Mirrors town_layout.SLOTS.
  { name: "grail", x: 886, y: 429, w: 180, band: "back", emph: 4 / 3 },
  { name: "special_5", x: 1060, y: 432, w: 140, band: "back" },
  { name: "special_4", x: 1233, y: 437, w: 140, band: "back" },
  // middle band - the middle shelf, the hall at its centre
  { name: "dwelling_1", x: 60, y: 564, w: 120, band: "middle" },
  { name: "dwelling_5", x: 282, y: 554, w: 125, band: "middle" },
  { name: "dwelling_6", x: 508, y: 545, w: 130, band: "middle" },
  { name: "hall", x: 757, y: 545, w: 170, band: "middle" },
  { name: "dwelling_3", x: 1004, y: 546, w: 125, band: "middle" },
  { name: "special_2", x: 1222, y: 548, w: 115, band: "middle" },
  // front band - the near shelf along the bottom edge
  { name: "special_1", x: 70, y: 710, w: 105, band: "front" },
  { name: "blacksmith", x: 271, y: 713, w: 112, band: "front" },
  { name: "dwelling_4", x: 483, y: 716, w: 120, band: "front" },
  { name: "dwelling_7", x: 723, y: 716, w: 155, band: "front" },
  { name: "marketplace", x: 973, y: 714, w: 135, band: "front" },
  { name: "tavern", x: 1200, y: 710, w: 120, band: "front" },
]);

/**
 * How large a band's art is drawn, relative to the slot table's own widths.
 *
 * Every one of the three has to land on a repeating device-pixel period at
 * the 3/2 stage, or a one-pixel gold trim comes out two device pixels thick in
 * some places and one in others along the same moulding. 2/3, 1 and 4/3 give
 * device scales of 1, 3/2 and 2 - periods of one, two and one. 5/6 (device
 * 5/4) is the value to keep out. Front against back is 2:1, which with the
 * shelves is the whole depth ramp: the same hut drawn at the near edge and on
 * the top shelf is half the size and a hundred and eighty rows higher.
 */
export const BAND_SCALE: Readonly<Record<TownSlot["band"], number>> = Object.freeze({
  back: 2 / 3,
  middle: 1,
  front: 4 / 3,
});

export const bandScale = (slot: TownSlot): number =>
  slot.scale ?? BAND_SCALE[slot.band] * (slot.emph ?? 1);

/**
 * The ground curve of each shelf: y(x) = centre + bow * ((x - 640) / 640)^2.
 *
 * The top shelf is a hill crest - higher at the centre, where the keep
 * stands, so the edges drop away (bow > 0); the near shelf dips toward the
 * viewer at the centre, where the road comes in (bow < 0). Small numbers on
 * purpose: the shelves are painted nearly level and the bow is what keeps a
 * band of six from standing on a ruled line.
 */
export const TERRACE_BOW: Readonly<Record<TownSlot["band"], number>> = Object.freeze({
  back: 10, middle: 4, front: -8,
});

export const terraceBow = (band: TownSlot["band"], x: number): number =>
  TERRACE_BOW[band] * ((x - TOWN_CANVAS_W / 2) / (TOWN_CANVAS_W / 2)) ** 2;

/**
 * Aerial perspective, as a 0..1 amount of haze.
 *
 * Distance in a painting is contrast, not just size: the far band loses a
 * little saturation and gains a little of the sky, which is also what stops the
 * back band fighting the front one for attention.
 */
export const BAND_HAZE: Readonly<Record<TownSlot["band"], number>> = Object.freeze({
  back: 1,
  middle: 0.3,
  front: 0,
});

/**
 * Where the airlight sits, in canvas rows.
 *
 * Aerial perspective is not a filter on a sprite, it is a wash of the sky's own
 * colour laid BETWEEN the depth planes: everything behind the wash takes it,
 * everything in front of it does not. So the amount is a function of the
 * canvas row, and the three rows that matter are the three bands' own ground
 * lines - derived from the slot table rather than written down, because the
 * table is what actually decides where a band stands.
 *
 * `AIR_FULL_Y` is the farthest band's floor: everything at or above it is at
 * the town's maximum distance and takes the whole wash. `AIR_HALF_Y` is the
 * middle band's floor and takes a third of it. `AIR_CLEAR_Y` is the front
 * band's floor, where the air is out of the way entirely - the near shelf a
 * player's eye rests on is never washed.
 *
 * `AIR_SPLIT_Y` is where the painter's order is cut to insert the second wash:
 * the back band's items are drawn, then the wash, then everything nearer. It
 * sits in the empty rows between the back band's lowest ground line and the
 * middle band's highest - the scarp between the two shelves - so cutting there
 * cannot reorder two items that overlap.
 */
const bandFloor = (band: TownSlot["band"]): number =>
  Math.max(...TOWN_SLOTS.filter((s) => s.band === band).map((s) => s.y));
const bandRoof = (band: TownSlot["band"]): number =>
  Math.min(...TOWN_SLOTS.filter((s) => s.band === band).map((s) => s.y));

export const AIR_FULL_Y = bandFloor("back");
export const AIR_HALF_Y = bandFloor("middle");
export const AIR_CLEAR_Y = bandFloor("front");
export const AIR_SPLIT_Y = Math.round((bandFloor("back") + bandRoof("middle")) / 2);

/** How much of the wash survives at the middle band's ground line. */
export const AIR_HALF_AMOUNT = 0.34;

const SLOT_BY_NAME: ReadonlyMap<string, TownSlot> = new Map(
  TOWN_SLOTS.map((s) => [s.name, s]),
);

export const slotByName = (name: string): TownSlot | null =>
  SLOT_BY_NAME.get(name) ?? null;

/**
 * Expected sprite height as a multiple of slot width.
 *
 * The 75th percentile of the shipped sprites in each slot across the thirteen
 * factions (measured 2026-09-03), which is what the layout gate was solved
 * against: towers are tall, market stalls are not.
 */
const HEIGHT_RATIO: Readonly<Record<string, number>> = Object.freeze({
  hall: 1.08, fort: 1.05, tavern: 1.03, blacksmith: 1.06, marketplace: 1.06,
  resource_silo: 1.12, mage_guild: 1.54, special_1: 1.10, special_2: 1.08,
  special_3: 1.22, special_4: 0.99, special_5: 1.05, dwelling_1: 1.05,
  dwelling_2: 1.09, dwelling_3: 1.19, dwelling_4: 1.11, dwelling_5: 1.15,
  dwelling_6: 1.11, dwelling_7: 1.08, grail: 1.23,
});
const DEFAULT_RATIO = 1.05;

/** Used for the hover ghost of an unbuilt slot, where there is no frame yet. */
export const expectedHeight = (slot: TownSlot): number =>
  slot.h ?? Math.trunc(slot.w * (HEIGHT_RATIO[slot.name] ?? DEFAULT_RATIO));

/** Painter's order: farther (smaller ground y) first, ties on x. */
export function drawOrder<T extends { readonly slot: TownSlot }>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => a.slot.y - b.slot.y || a.slot.x - b.slot.x);
}

// --------------------------------------------------- spreading one faction

const BANDS: readonly TownSlot["band"][] = Object.freeze(["back", "middle", "front"]);

/** An alley has to stay walkable however tight the band gets. */
const MIN_ALLEY = 40;
/**
 * How far inside the canvas a band starts.
 *
 * Zero: a classic town screen has town against both edges of the frame, and the
 * building art already carries a transparent margin either side of its
 * masonry, so a band laid out flush with the canvas still leaves the air a
 * frame needs.
 */
const MARGIN = 0;
/**
 * The widest a band will pull its buildings apart before it gives up and
 * centres itself instead. A band that cannot fill the canvas is a band that
 * should be narrower than the canvas, with ground either side of it.
 */
const MAX_GAP = 124;
const MAX_ALLEY_GAP = 128;

/**
 * Which interior gaps of a band are alleys: the ones the road runs through.
 *
 * By thirds rather than by slot name, because the point of them is to space the
 * band evenly, and a faction that has no `special_4` must still get its alleys
 * somewhere sensible rather than inheriting a hole where the missing slot was.
 */
function alleyGaps(n: number, count: number): readonly number[] {
  if (n < 3) return [];
  if (count === 1) return [Math.round(n / 2)];
  const a = Math.max(1, Math.round(n / 3));
  const b = Math.min(n - 1, Math.round((2 * n) / 3));
  return a === b ? [a] : [a, b];
}

/** What a slot's art actually covers once its band's scale is applied. */
export const drawnWidth = (slot: TownSlot): number => slot.w * bandScale(slot);

export interface Footprint { readonly left: number; readonly right: number }

/** The drawn extent of a slot, which is what the eye judges a band's gaps by. */
export function footprint(slot: TownSlot): Footprint {
  const half = drawnWidth(slot) / 2;
  return { left: slot.x - half, right: slot.x + half };
}

/**
 * Re-spread one faction's slots across the canvas.
 *
 * Only six of Castle's eight back-band slots exist, and laying its buildings
 * out on the all-19 grid leaves two holes in the skyline where Saltreach's extra
 * specials would have stood - which is exactly what makes a town screen read
 * as a shelf with gaps in it. So a band is spread over the slots the faction
 * *has*, measured by what each one actually *draws*, the alleys hold their
 * width, and a band with more canvas than it can use centres itself rather
 * than stretching into a shelf.
 *
 * `w` is untouched. `y` follows the terrace curve: the table's `y` carries the
 * bow at the table's own `x`, so a slot that moves sideways keeps its stagger
 * and takes the bow of its new column.
 */
export function spreadSlots(present: ReadonlySet<string>): readonly TownSlot[] {
  const out: TownSlot[] = [];
  for (const band of BANDS) {
    const row = TOWN_SLOTS.filter((s) => s.band === band && present.has(s.name));
    if (row.length === 0) continue;
    // The keep anchors the top centre whatever the faction has around it.
    // Castle has six back-band slots and Saltreach eight, and spreading either
    // row as one run put the keep a building's width off the axis; so the
    // back band is two runs, one each side of a fort pinned to the middle.
    const fortAt = band === "back" ? row.findIndex((s) => s.name === "fort") : -1;
    if (fortAt < 0) {
      spreadRun(row, band, MARGIN, TOWN_CANVAS_W - MARGIN, band === "front" ? 1 : 2, out);
      continue;
    }
    const fort = row[fortAt]!;
    const half = drawnWidth(fort) / 2;
    const fx = TOWN_CANVAS_W / 2;
    out.push({ ...fort, x: fx, y: Math.round(fort.y - terraceBow(band, fort.x) + terraceBow(band, fx)) });
    spreadRun(row.slice(0, fortAt), band, MARGIN, fx - half - FORT_CLEARANCE, 1, out);
    spreadRun(row.slice(fortAt + 1), band, fx + half + FORT_CLEARANCE, TOWN_CANVAS_W - MARGIN, 1, out);
  }
  return out;
}

/** Air either side of the keep, so a neighbour never touches it. */
const FORT_CLEARANCE = 24;

/** Spread one run of slots evenly between `from` and `to`, holding its alleys. */
function spreadRun(
  row: readonly TownSlot[], band: TownSlot["band"], from: number, to: number,
  alleyCount: number, out: TownSlot[],
): void {
  const n = row.length;
  if (n === 0) return;
  const widths = row.map(drawnWidth);
  const total = widths.reduce((sum, w) => sum + w, 0);
  const inner = n - 1;
  const span = to - from;
  const spare = span - total;
  const alleys = alleyGaps(n, alleyCount);
  const unit = inner === 0 ? 0 : spare / inner;
  // With nothing but alleys between them, holding an alley open would push
  // the band off the canvas, so the band falls back to even gaps.
  const others = inner - alleys.length;
  const alleyW = Math.min(
    MAX_ALLEY_GAP,
    others > 0 ? Math.max(MIN_ALLEY, unit) : unit,
  );
  const normal = Math.min(
    MAX_GAP,
    others > 0 ? (spare - alleys.length * alleyW) / others : unit,
  );
  const used = total + alleys.length * alleyW + Math.max(0, others) * normal;
  let cursor = from + Math.max(0, (span - used) / 2);

  for (let i = 0; i < n; i++) {
    const slot = row[i]!;
    if (i > 0) cursor += alleys.includes(i) ? alleyW : normal;
    const x = Math.round(cursor + widths[i]! / 2);
    const y = Math.round(slot.y - terraceBow(band, slot.x) + terraceBow(band, x));
    out.push({ ...slot, x, y });
    cursor += widths[i]!;
  }
}

// ---------------------------------------------------- per-faction layouts

/**
 * The slots one faction's buildings stand on.
 *
 * PER-FACTION LAYOUTS (2026-09-04). Thirteen paintings with thirteen cameras
 * - a harbour bay seen from a headland, a cavern from its floor, a peak from
 * the air - so the slot table is solved per faction against its own
 * painting (TOWNS/tools/town_layout.py STANDS, exported to townLayouts.ts)
 * and the shared table with its spread is only the fallback for a faction
 * that has none. `present` filters to the slots the faction actually has;
 * the solved layout already carries the faction's own x, y and scale.
 */
export function layoutSlots(faction: string, present: ReadonlySet<string>): readonly TownSlot[] {
  const own = TOWN_LAYOUTS[faction];
  if (own === undefined) return spreadSlots(present);
  return own.slots.filter((s) => present.has(s.name)).map((s) => ({
    name: s.name, x: s.x, y: s.y, w: s.w, band: s.band, scale: s.scale, h: s.h,
  }));
}

/** Whether a faction has a layout of its own rather than the shared table. */
export const hasOwnLayout = (faction: string): boolean => TOWN_LAYOUTS[faction] !== undefined;

/** The canvas rows the faction's clouds and birds fly in: under the stage crop, over its horizon. */
export function skyBand(faction: string): readonly [number, number] {
  const own = TOWN_LAYOUTS[faction];
  return own === undefined ? [205, HORIZON_Y - 60] : own.sky;
}

/**
 * The airlight's rows for one faction: where the wash is full, where it is
 * a third, where it is gone, and where the painter's order is cut for the
 * second wash. Derived from the faction's own layout the way the shared
 * constants below are derived from the shared table, because a layout that
 * puts its far band at row 300 and its near band at 700 needs the haze to
 * follow it rather than the table it replaced.
 */
export interface AirRows {
  readonly full: number;
  readonly half: number;
  readonly clear: number;
  readonly split: number;
}

export function airRows(faction: string): AirRows {
  const own = TOWN_LAYOUTS[faction];
  if (own === undefined) {
    return { full: AIR_FULL_Y, half: AIR_HALF_Y, clear: AIR_CLEAR_Y, split: AIR_SPLIT_Y };
  }
  const ys = (band: TownSlot["band"]): number[] => own.slots.filter((s) => s.band === band).map((s) => s.y);
  const back = ys("back");
  const middle = ys("middle");
  const front = ys("front");
  const full = back.length > 0 ? Math.max(...back) : AIR_FULL_Y;
  // The rows must run downhill, whatever the stands do: a middle stand can
  // sit level with a back stand on a composition with two far shelves.
  const half = Math.max(full + 1, middle.length > 0 ? Math.max(...middle) : full + 1);
  const clear = Math.max(half + 1, front.length > 0 ? Math.max(...front) : TOWN_CANVAS_H);
  const middleRoof = middle.length > 0 ? Math.min(...middle) : half;
  const split = Math.max(full, Math.round((full + Math.max(full, middleRoof)) / 2));
  return { full, half, clear, split };
}

// ------------------------------------------------------- chains and slots

/**
 * The upgrade chain a building belongs to, base first.
 *
 * `upgradeOf` is a single parent link, so a chain is a walk to the root. Saltreach's
 * Powder Store sits three deep (Frigate -> upgrade -> Warehouse), which
 * is why depth is walked rather than assumed to be 0 or 1.
 */
export function chainDepth(
  content: ContentRegistry, id: BuildingDefId, guard = 16,
): number {
  let depth = 0;
  let cur: BuildingDefId | null = id;
  while (cur !== null && depth < guard) {
    const def: BuildingDef | undefined = content.buildings.get(cur);
    if (def === undefined) break;
    cur = def.upgradeOf;
    if (cur !== null) depth++;
  }
  return depth;
}

export interface ResolvedSlot {
  readonly slot: TownSlot;
  /** The one building drawn here, or null when nothing in the chain is built. */
  readonly built: BuildingDef | null;
  /** Everything this faction could put in this slot, base first. */
  readonly chain: readonly BuildingDef[];
  /** The next step up from `built`, or the chain's base when nothing is built. */
  readonly next: BuildingDef | null;
}

const byId = (a: BuildingDef, b: BuildingDef): number =>
  (a.id as string) < (b.id as string) ? -1 : (a.id as string) > (b.id as string) ? 1 : 0;

/**
 * Resolve every slot for one town.
 *
 * The rule that makes the screen work: a slot draws exactly ONE building - the
 * highest built level in its chain - and an unbuilt structure is simply not
 * drawn. `addBuilding` already drops the step an upgrade replaces from `built`,
 * but a captured town or a hand-built fixture can carry both, so the highest
 * built step wins by chain depth rather than by trusting `built` to hold one.
 */
export function resolveTownSlots(
  content: ContentRegistry,
  faction: string,
  built: readonly BuildingDefId[],
): readonly ResolvedSlot[] {
  const chains = new Map<string, BuildingDef[]>();
  for (const id of [...content.buildings.keys()].sort()) {
    const def = content.buildings.get(id);
    if (def === undefined || (def.faction as string) !== faction) continue;
    const list = chains.get(def.slot);
    if (list === undefined) chains.set(def.slot, [def]);
    else list.push(def);
  }

  const isBuilt = new Set<string>(built.map((b) => b as string));
  const out: ResolvedSlot[] = [];
  // The layout is a function of what the faction *has*, never of what it has
  // built, so a building never slides sideways the turn after it goes up.
  for (const slot of layoutSlots(faction, new Set(chains.keys()))) {
    const chain = (chains.get(slot.name) ?? [])
      .slice()
      .sort((a, b) => chainDepth(content, a.id) - chainDepth(content, b.id) || byId(a, b));
    if (chain.length === 0) continue;

    let top: BuildingDef | null = null;
    let topDepth = -1;
    for (const def of chain) {
      if (!isBuilt.has(def.id as string)) continue;
      const d = chainDepth(content, def.id);
      if (d > topDepth) { top = def; topDepth = d; }
    }

    let next: BuildingDef | null = null;
    for (const def of chain) {
      if (isBuilt.has(def.id as string)) continue;
      if (top === null) { if (def.upgradeOf === null) { next = def; break; } continue; }
      if (def.upgradeOf === top.id) { next = def; break; }
    }
    if (next === null && top === null) next = chain[0] ?? null;

    out.push({ slot, built: top, chain, next });
  }
  return out;
}

/** Slots with something to draw, in painter's order. */
export const paintedSlots = (slots: readonly ResolvedSlot[]): readonly ResolvedSlot[] =>
  drawOrder(slots);

// ------------------------------------------------------------------ staging

export interface StageLayout {
  /**
   * Canvas pixels to screen pixels. Fractional: the painting is one element
   * under one `transform: scale()`, so nothing is resampled per sprite and the
   * slot grid stays in canvas coordinates whatever the window is.
   */
  readonly scale: number;
  /** The viewport the painting is seen through - the whole space, always. */
  readonly viewW: number;
  readonly viewH: number;
  /** Where the canvas' top-left lands in the viewport; zero or negative. */
  readonly offsetX: number;
  readonly offsetY: number;
}

/**
 * Cover the space the screen has with the 1280x720 town painting.
 *
 * The old rule fitted the painting at a whole scale, which meant 1x in a
 * 1920x1080 window: a 1280x720 postage stamp in a black surround, with the town
 * reading as a diorama on a shelf rather than as a place. A town screen has to
 * fill the frame, so the painting is scaled to *cover* and the overflow is
 * cropped, anchored to the bottom: the sky goes first and the front band - the
 * one the player clicks - is the last thing to be lost. The scale is not
 * rounded, because rounding it up is what starts cropping buildings off the
 * left and right edges instead of sky off the top.
 */
export function stageLayout(availW: number, availH: number): StageLayout {
  const w = availW > 0 ? availW : TOWN_CANVAS_W;
  const h = availH > 0 ? availH : TOWN_CANVAS_H;
  const cover = Math.max(w / TOWN_CANVAS_W, h / TOWN_CANVAS_H);
  const scale = quantise(cover, w);
  const canvasW = TOWN_CANVAS_W * scale;
  const canvasH = TOWN_CANVAS_H * scale;
  return {
    scale,
    viewW: w,
    viewH: h,
    offsetX: Math.round((w - canvasW) / 2),
    offsetY: Math.round(h - canvasH),
  };
}

/**
 * Snap the stage to a quarter step when snapping is free.
 *
 * A free float over `image-rendering: pixelated` art makes a one-pixel gold
 * trim line come out two device pixels thick in some places and one in others
 * along the same moulding. A quarter step repeats on a fixed period instead:
 * 1920/1280 is exactly 3/2, so at 1080p every two source pixels become three
 * device pixels everywhere in the frame, and the three band scales (2/3, 5/6,
 * 1) land on 1, 5/4 and 3/2 device.
 *
 * Snapping can only ever round *up* - the painting has to keep covering the
 * viewport - so it costs a horizontal crop, and a crop that eats the outermost
 * building is worse than the shimmer it cures. Past a 3% overshoot the exact
 * cover scale wins.
 */
const QUANTISE_SLACK = 0.03;

function quantise(cover: number, viewW: number): number {
  const stepped = Math.ceil(cover * 4 - 1e-6) / 4;
  const overshoot = (TOWN_CANVAS_W * stepped - viewW) / viewW;
  return overshoot <= QUANTISE_SLACK ? stepped : cover;
}

/**
 * How the painted backdrop is fitted into the rows the stage actually shows.
 *
 * It is not: the backdrop is drawn 1:1 on the canvas and the stage's crop
 * takes the sky off the top. This used to translate the painting down by the
 * cropped rows and squeeze it into what was left, because the old backdrops
 * put their horizon at row 80-234 and the crop would have lost it. The
 * terraced backdrops are COMPOSED for the crop - the horizon is at
 * `HORIZON_Y`, the shelves below it, and rows 0-200 are expendable sky - so
 * squeezing them would only flatten the scarps that give the screen its
 * height, and would slide the painted shelves out from under the buildings
 * that stand on them. `shift` and `squeeze` are kept as the identity so the
 * scrims that read them (WarmScrim) need no second contract.
 */
export interface SkyLift {
  readonly shift: number;
  readonly squeeze: number;
}

export function skyLift(_layout: StageLayout): SkyLift {
  return { shift: 0, squeeze: 1 };
}
